import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './App.css';
import './InfoPage.css';
import Navigation from './Navigation';
import LanguageSelector from './LanguageSelector';
import { useAnalytics, usePageTracking } from './hooks/useAnalytics';
import useTranslation from './hooks/useTranslation';

function InfoPage() {
  const location = useLocation();
  
  // Analytics and translation hooks
  const { trackFooterLink } = useAnalytics();
  const { t, isGerman } = useTranslation();
  
  // Track page view
  usePageTracking('info', { 
    page_title: t('infopage.title', 'ChronoLog - About & FAQ')
  });

  // Handle hash scrolling
  useEffect(() => {
    const scrollToSection = () => {
      if (location.hash) {
        const element = document.querySelector(location.hash);
        if (element) {
          const headerHeight = 100; // From InfoPage.css scroll-padding-top
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - headerHeight;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
    };

    // A small delay to ensure the DOM is ready for scrolling
    const timeoutId = setTimeout(scrollToSection, 100);

    return () => clearTimeout(timeoutId);
  }, [location.hash]);

  return (
    <div className="info-page">
      <Helmet>
        <title>{t('infopage.title', 'About & FAQ - ChronoLog')}</title>
        <meta name="description" content={t('infopage.metaDescription', 'Learn more about ChronoLog, how it works, and get answers to frequently asked questions about tracking watch accuracy and performance.')} />
        <link rel="canonical" href={`https://www.chronolog.watch/info${isGerman ? '?lang=de' : ''}`} />
        <link rel="alternate" hrefLang="en" href="https://www.chronolog.watch/info" />
        <link rel="alternate" hrefLang="de" href="https://www.chronolog.watch/info?lang=de" />
        <link rel="alternate" hrefLang="x-default" href="https://www.chronolog.watch/info" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": isGerman ? "Warum sehe ich große und unterschiedliche Messungen?" : "Why am I seeing large and varied measurements?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": isGerman ? "Die wahrscheinlichste Ursache ist, dass Messungen zu nah beieinander genommen werden. Jede Messung hat einen inhärenten menschlichen Fehler, aber über längere Zeiträume werden diese Fehler vernachlässigbar. Wir empfehlen, mindestens 8 Stunden zwischen den Messungen zu warten, um die genauesten Ergebnisse zu erzielen." : "The most likely cause is taking measurements too close together. Every measurement has inherent human error, but over longer time periods, these errors become negligible. We recommend waiting at least 8 hours between measurements for the most accurate results."
                }
              },
              {
                "@type": "Question",
                "name": isGerman ? "Was sind die COSC-, METAS- und Superlative-Tags, die ich sehe?" : "What are the COSC, METAS, and Superlative tags I see?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": isGerman ? "Das sind prestigeträchtige Industriestandards, für deren Zertifizierung Uhrenmarken bezahlen. Jeder Standard hat spezifische Genauigkeitsanforderungen und Testbedingungen. Wenn Ihre Zeitmessungen diese professionellen Standards erfüllen, zeigt ChronoLog das entsprechende Zertifizierungs-Tag an—und gibt Ihnen Einblick darüber, wie Ihre Uhr im Vergleich zu diesen Elite-Benchmarks abschneidet." : "These are prestigious industry standards that watch brands pay to have their timepieces certified against. Each standard has specific accuracy requirements and testing conditions. When your timing measurements meet these professional standards, ChronoLog displays the appropriate certification tag—giving you insight into how your watch performs compared to these elite benchmarks."
                }
              },
              {
                "@type": "Question",
                "name": isGerman ? "Ist das ein Timegrapher? Sollte ich stattdessen einen Timegrapher kaufen?" : "Is this a timegrapher? Should I get a timegrapher instead?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": isGerman ? "ChronoLog ist kein Timegrapher. Timegrapher sind professionelle Werkzeuge, die sofortige, präzise Messungen liefern, aber spezialisierte Hardware erfordern. ChronoLog bietet einen entspannteren und spaßigeren Ansatz, der vollständig auf Ihrem Telefon funktioniert und die Verfolgung der Uhrenleistung für jeden ohne zusätzliche Ausrüstung zugänglich macht." : "ChronoLog is not a timegrapher. Timegraphers are professional tools that provide instant, precise measurements but require specialized hardware. ChronoLog offers a more casual and fun approach that works entirely on your phone, making watch performance tracking accessible to everyone without additional equipment."
                }
              },
              {
                "@type": "Question",
                "name": isGerman ? "Wie genau ist ChronoLog im Vergleich zu professionellen Zeitmessgeräten?" : "How accurate is ChronoLog compared to professional timing equipment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": isGerman ? "ChronoLog verwendet Atomuhr-Synchronisation über NTP, um Mikrosekunden-Genauigkeit zu erreichen. Während professionelle Timegrapher sofortige Ablesungen liefern, können ChronoLogs Messungen über Zeiträume von 8+ Stunden gleichermaßen genau für die Verfolgung von Tagesraten und langfristigen Leistungstrends sein." : "ChronoLog uses atomic clock synchronization via NTP to achieve microsecond-level accuracy. While professional timegraphers provide instant readings, ChronoLog's measurements over time periods of 8+ hours can be equally accurate for tracking daily rate and long-term performance trends."
                }
              },
              {
                "@type": "Question",
                "name": isGerman ? "Kann ChronoLog mit jeder Art von Uhr funktionieren?" : "Can ChronoLog work with any type of watch?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": isGerman ? "Ja, ChronoLog funktioniert mit jeder Uhr, die Zeit anzeigt - mechanisch, automatisch, Quarz oder sogar Smartwatches. Die App misst Zeitabweichungen unabhängig vom Uhrwerktyp und macht sie nützlich für die Verfolgung der Genauigkeit in Ihrer gesamten Sammlung." : "Yes, ChronoLog works with any watch that displays time - mechanical, automatic, quartz, or even smartwatches. The app measures time deviation regardless of the movement type, making it useful for tracking accuracy across your entire collection."
                }
              },
              {
                "@type": "Question",
                "name": isGerman ? "Was ist der Unterschied zwischen Quick-Tap- und Foto-Messmodi?" : "What's the difference between quick-tap and photo measurement modes?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": isGerman ? "Der Quick-Tap-Modus ermöglicht es Ihnen, die Zeit Ihrer Uhr manuell gegen atomare Referenzzeit aufzuzeichnen, indem Sie tippen, wenn sich die Sekunden ausrichten. Der Foto-Modus verwendet KI, um ein Bild Ihres Uhren-Zifferblatts zu analysieren und automatisch den Zeitversatz zu berechnen. Beide Methoden bieten genaue Messungen für verschiedene Benutzerpräferenzen." : "Quick-tap mode lets you manually record your watch time against atomic reference time by tapping when the seconds align. Photo mode uses AI to analyze a picture of your watch dial and automatically calculate the time offset. Both methods provide accurate measurements for different user preferences."
                }
              },
              {
                "@type": "Question",
                "name": "How does the audio timegrapher measurement work?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "ChronoLog's audio mode listens to the ticking sound of your mechanical watch through your phone's microphone — similar to how a professional timegrapher works. It analyzes the timing between ticks to calculate the rate in seconds per day, and tells you how confident it is in each reading."
                }
              },
              {
                "@type": "Question",
                "name": "What are the limitations of the audio measurement?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Audio measurement works best in a quiet environment. Background noise, loud ticking from other clocks, or very quiet movements can affect accuracy. For best results: place your watch directly on a hard surface near your phone's microphone, minimize background noise, and hold the phone steady. The audio method is best suited for mechanical watches with audible ticks."
                }
              }
            ]
          })}
        </script>
      </Helmet>
      {/* Navigation */}
      <Navigation showAndroidButton={false} />
      
      {/* Language Selector */}
      <div className="language-selector-container">
        <LanguageSelector />
      </div>

      {/* About Section */}
      <section id="about" className="info-section">
        <div className="container">
          <h2>{t('infopage.about.title', 'About ChronoLog')}</h2>
          <div className="content">
            <p>{t('infopage.about.intro', 'ChronoLog transforms how you understand and care for your timepieces. Whether you own a vintage Rolex, a modern Omega, or your first mechanical watch, ChronoLog helps you track, analyze, and optimize your watch\'s performance over time.')}</p>
            
            <h3>{t('infopage.about.whatItDoes.title', 'What ChronoLog Does')}</h3>
            <p>{t('infopage.about.whatItDoes.description', 'At its core, ChronoLog provides precision timing measurements by comparing your watch\'s time against atomic reference standards. With just a few taps, you can capture point-in-time accuracy readings that reveal how your watch performs in real-world conditions.')}</p>
            
            <h3>{t('infopage.about.analytics.title', 'Performance Analytics That Matter')}</h3>
            <p>{t('infopage.about.analytics.description', 'Each measurement builds a comprehensive picture of your watch\'s behavior. Track daily rate changes, identify patterns related to different wearing conditions, and receive alerts when sudden changes might indicate a problem requiring attention. Our intelligent tagging system lets you understand how factors like temperature, activity, or winding affect performance.')}</p>
            
            <h3>{t('infopage.about.collection.title', 'Your Complete Watch Collection')}</h3>
            <p>{t('infopage.about.collection.description', 'Beyond performance tracking, ChronoLog serves as your digital watch vault. Document your collection with photos, track purchase details, and maintain a complete history of each timepiece\'s accuracy journey. Whether you\'re a casual enthusiast or serious collector, ChronoLog helps you stay connected to what matters most about your watches.')}</p>
            
            <p><strong>{t('infopage.about.conclusion', 'ChronoLog makes the invisible visible—turning every watch into a source of fascinating data and helping you become a more informed owner.')}</strong></p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="info-section">
        <div className="container">
          <h2>{t('infopage.faq.title', 'Frequently Asked Questions')}</h2>
          <div className="content">
            <div className="faq-item">
              <h3>{t('infopage.faq.items.0.question', 'Why am I seeing large and varied measurements?')}</h3>
              <p>{t('infopage.faq.items.0.answer', 'The most likely cause is taking measurements too close together. Every measurement has inherent human error, but over longer time periods, these errors become negligible. We recommend waiting at least 8 hours between measurements for the most accurate results.')}</p>
            </div>
            
            <div className="faq-item">
              <h3>{t('infopage.faq.items.1.question', 'What are the COSC, METAS, and Superlative tags I see?')}</h3>
              <p>{t('infopage.faq.items.1.answer', 'These are prestigious industry standards that watch brands pay to have their timepieces certified against. Each standard has specific accuracy requirements and testing conditions. When your timing measurements meet these professional standards, ChronoLog displays the appropriate certification tag—giving you insight into how your watch performs compared to these elite benchmarks.')}</p>
            </div>
            
            <div className="faq-item">
              <h3>{t('infopage.faq.items.2.question', 'What do Offset, Rate, and Change mean?')}</h3>
              <p>{t('infopage.faq.items.2.answer', 'Offset is the total time difference between your watch and the reference time. Change shows how much the offset shifted since your last measurement. Rate calculates your watch\'s average daily gain or loss by dividing the total change by the time elapsed—expressed in seconds per day.')}</p>
            </div>
            
            <div className="faq-item">
              <h3>{t('infopage.faq.items.3.question', 'Is this a timegrapher? Should I get a timegrapher instead?')}</h3>
              <p>{t('infopage.faq.items.3.answer', 'ChronoLog is not a timegrapher. Timegraphers are professional tools that provide instant, precise measurements but require specialized hardware. ChronoLog offers a more casual and fun approach that works entirely on your phone, making watch performance tracking accessible to everyone without additional equipment.')}</p>
            </div>
            
            <div className="faq-item">
              <h3>{t('infopage.faq.items.4.question', 'Why isn\'t ChronoLog free?')}</h3>
              <p>{t('infopage.faq.items.4.answer', 'Honestly? I\'d like to buy more watches and can use the revenue from this app to do it guilt-free! But seriously, the premium features help support ongoing development, new features, and keeping the app ad-free while maintaining the highest quality experience.')}</p>
            </div>

            <div className="faq-item">
              <h3>{t('infopage.faq.items.5.question', 'How does the audio timegrapher measurement work?')}</h3>
              <p>{t('infopage.faq.items.5.answer', 'ChronoLog\'s audio mode listens to the ticking sound of your mechanical watch through your phone\'s microphone — similar to how a professional timegrapher works. It analyzes the timing between ticks to calculate the rate in seconds per day, and tells you how confident it is in each reading.')}</p>
            </div>

            <div className="faq-item">
              <h3>{t('infopage.faq.items.6.question', 'What are the limitations of the audio measurement?')}</h3>
              <p>{t('infopage.faq.items.6.answer', 'Audio measurement works best in a quiet environment. Background noise, loud ticking from other clocks, or very quiet movements can affect accuracy. For best results: place your watch directly on a hard surface near your phone\'s microphone, minimize background noise, and hold the phone steady. The audio method is best suited for mechanical watches with audible ticks — it won\'t work well with quartz watches that tick once per second. Results may vary by movement and phone model, so treat it as a useful guide rather than an absolute reading.')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Policy Section */}
      <section id="privacy" className="info-section privacy-section">
        <div className="container">
          <h2>{t('infopage.privacy.title', 'Privacy Policy')}</h2>
          <div className="content">
            <p><strong>{t('infopage.privacy.lastUpdated', 'Last updated: June 2025')}</strong></p>
            
            <h3>{t('infopage.privacy.analytics.title', 'Usage Analytics, Debugging, and Product Development')}</h3>
            <p>{t('infopage.privacy.analytics.description', 'To help us improve ChronoLog, we collect anonymous in-app usage data entered by the user. This includes timepiece information and timing measurement data; excluding images which do not leave the device. These data are used for analytics, debugging, and product development purposes.')}</p>
            
            <p>{t('infopage.privacy.noPersonalInfo', 'No personal information is collected or stored.')}</p>
            
            <h3>{t('infopage.privacy.changes.title', 'Changes to This Privacy Policy')}</h3>
            <p>{t('infopage.privacy.changes.description', 'We may update our Privacy Policy from time to time. We will notify you of any changes by posting the updated Privacy Policy on this page.')}</p>
            
            <h3>{t('infopage.privacy.contact.title', 'Contact')}</h3>
            <p>{t('infopage.privacy.contact.description', 'Questions? Contact us at')} <a 
              href="mailto:tylerjaywood+chronologwebsite@gmail.com"
              onClick={() => trackFooterLink('email', 'tylerjaywood+chronologwebsite@gmail.com', { location: 'info_page' })}
            >
              tylerjaywood+chronologwebsite@gmail.com
            </a></p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="info-footer">
        <div className="container">
          <p>{t('infopage.footer.copyright', '© 2024 ChronoLog. All rights reserved.')}</p>
        </div>
      </footer>
    </div>
  );
}

export default InfoPage; 